import { baseApi, getErrorMessage } from '@/shared/api';
import { mapProduct } from '../libs/mapProduct';
import { mapProductApplication } from '../libs/mapProductApplication';
import { mapWebfunnelLink } from '../libs/mapWebfunnelLink';
import { mapProductWebhook } from '../libs/mapWebhook';
import type {
  Product,
  ProductApplication,
  ProductWebfunnelLink,
  ProductWebhook,
} from '../model/types';
import type {
  ProductDTO,
  ProductApplicationDTO,
  WebfunnelLinkDTO,
  WebfunnelLinkTypeDTO,
  WebApplicationTypeDTO,
  ProductWebhookDTO,
} from './types';

export const getProducts = async (): Promise<Product[]> => {
  const { data, error } = await baseApi.functions.invoke<{
    products: ProductDTO[];
  }>('products', {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data.products.map(mapProduct);
};

interface AddProductParams {
  name: string;
}

export const addProduct = async (
  params: AddProductParams,
): Promise<Product> => {
  const { data, error } = await baseApi.functions.invoke<{
    product: ProductDTO;
  }>('products', {
    body: {
      name: params.name,
    },
    method: 'POST',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return mapProduct(data.product);
};

interface AddIOSProductApplicationParams {
  productId: string;
  integrationId: string;
  platform: 'ios';
  bundleId: string;
  appstoreId: string;
  name: string;
  logoUrl: string;
}

interface AddAndroidProductApplicationParams {
  productId: string;
  integrationId: string;
  platform: 'android';
  bundleId: string;
  name: string;
  logoUrl: string;
}

interface AddWebProductApplicationParams {
  productId: string;
  platform: 'web';
  webUrl: string;
  name: string;
  applicationType: WebApplicationTypeDTO;
}

type AddProductApplicationParams =
  | AddIOSProductApplicationParams
  | AddAndroidProductApplicationParams
  | AddWebProductApplicationParams;

export const addProductApplication = async (
  params: AddProductApplicationParams,
): Promise<ProductApplication> => {
  const { data, error } = await baseApi.functions.invoke<{
    application: ProductApplicationDTO;
  }>('applications', {
    body: params,
    method: 'POST',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return mapProductApplication(data.application);
};

interface UpdateProductApplicationsParams {
  updates: {
    id: string;
    integrationId: string;
  }[];
}

export const updateProductApplications = async (
  params: UpdateProductApplicationsParams,
): Promise<void> => {
  const { error } = await baseApi.functions.invoke<{
    application: ProductApplicationDTO;
  }>('applications', {
    body: {
      updates: params.updates,
    },
    method: 'PATCH',
  });

  if (error) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }
};

interface DeleteProductApplicationParams {
  id: string;
}

export const deleteProductApplication = async (
  params: DeleteProductApplicationParams,
): Promise<void> => {
  const { error } = await baseApi.functions.invoke(
    `applications/${params.id}`,
    {
      method: 'DELETE',
    },
  );

  if (error) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }
};

interface AndroidApplicationDetails {
  bundleId: string;
  name: string;
  logoUrl: string;
}

export const getAndroidApplicationDetails = async (
  bundleId: string,
): Promise<AndroidApplicationDetails> => {
  const { data, error } =
    await baseApi.functions.invoke<AndroidApplicationDetails>(
      `applications/google-play-details?bundleId=${bundleId}`,
      {
        method: 'GET',
      },
    );

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return data;
};

interface AppStoreApplicationDetails {
  bundleId: string;
  name: string;
  appstoreId: string;
  logoUrl: string;
}

export const getAppStoreApplicationDetails = async (
  bundleId: string,
): Promise<AppStoreApplicationDetails> => {
  const { data, error } =
    await baseApi.functions.invoke<AppStoreApplicationDetails>(
      `applications/app-store-details?bundleId=${bundleId}`,
      {
        method: 'GET',
      },
    );

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return data;
};

export const getWebfunnelLinks = async (): Promise<ProductWebfunnelLink[]> => {
  const { data, error } = await baseApi.functions.invoke<{
    webfunnelLinks: WebfunnelLinkDTO[];
  }>(`webfunnel-links`, {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return data.webfunnelLinks.map(mapWebfunnelLink);
};

interface AddWebfunnelLinkParams {
  productId: string;
  webUrl: string;
  linkType: WebfunnelLinkTypeDTO;
}

export const addWebfunnelLink = async (
  params: AddWebfunnelLinkParams,
): Promise<ProductWebfunnelLink> => {
  const { data, error } = await baseApi.functions.invoke<{
    webfunnelLink: WebfunnelLinkDTO;
  }>('webfunnel-links', {
    body: params,
    method: 'POST',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return mapWebfunnelLink(data.webfunnelLink);
};

interface DeleteWebfunnelLinkParams {
  id: string;
}

export const deleteWebfunnelLink = async (
  params: DeleteWebfunnelLinkParams,
): Promise<void> => {
  const { error } = await baseApi.functions.invoke(
    `webfunnel-links/${params.id}`,
    {
      method: 'DELETE',
    },
  );

  if (error) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }
};

interface UpdateWebfunnelLinkParams {
  id: string;
  webUrl: string;
  linkType: WebfunnelLinkTypeDTO;
}

export const updateWebfunnelLink = async (
  params: UpdateWebfunnelLinkParams,
): Promise<ProductWebfunnelLink> => {
  const { data, error } = await baseApi.functions.invoke<{
    webfunnelLink: WebfunnelLinkDTO;
  }>(`webfunnel-links/${params.id}`, {
    body: params,
    method: 'PUT',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return mapWebfunnelLink(data.webfunnelLink);
};

interface GetApplicationsParams {
  productId?: string;
  integrationId?: string;
}

export const getApplications = async (
  params: GetApplicationsParams,
): Promise<ProductApplication[]> => {
  const queryParam = params.productId
    ? `productId=${params.productId}`
    : `integrationId=${params.integrationId}`;
  const { data, error } = await baseApi.functions.invoke<{
    applications: ProductApplicationDTO[];
  }>(`applications?${queryParam}`, {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return data.applications.map(mapProductApplication);
};

interface GetWebhooksParams {
  productId: string;
}
export const getWebhooks = async (
  params: GetWebhooksParams,
): Promise<ProductWebhook[]> => {
  const { data, error } = await baseApi.functions.invoke<{
    webhooks: ProductWebhookDTO[];
  }>(`webhooks?productId=${params.productId}`, {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return data.webhooks.map(mapProductWebhook);
};

interface AddWebhookParams {
  productId: string;
  sourceIntegrationId: string;
  webhookPath: string;
  appHash: string;
  applicationIds: string[];
}

export const addWebhook = async (
  params: AddWebhookParams,
): Promise<ProductWebhook> => {
  const { data, error } = await baseApi.functions.invoke<{
    webhook: ProductWebhookDTO;
  }>('webhooks', {
    body: params,
    method: 'POST',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }

  return mapProductWebhook(data.webhook);
};

interface DeleteWebhookParams {
  id: string;
}

export const deleteWebhook = async (
  params: DeleteWebhookParams,
): Promise<void> => {
  const { error } = await baseApi.functions.invoke(`webhooks/${params.id}`, {
    method: 'DELETE',
  });

  if (error) {
    const errorMessage = getErrorMessage(error);
    throw new Error(errorMessage);
  }
};
